import { Canvas, ThreeEvent } from "@react-three/fiber";
import './layer-editor.css';
import { POI } from "../poi/poi";
import { BoxHelper } from "./box-helper/box-helper";
import { Menu } from "./menu/menu";
import * as THREE from 'three';
import { forwardRef, useImperativeHandle, useRef } from "react";
import { LayerEditorOrbitControlsRef, OrbitControls } from "./controls/orbit-controls";
import { TransformControls } from "./controls/transform-controls";
import { WorkInstructionWithUUIDs } from "../model-space/model-space";
import { LayerEditorExposerRef, LayerEditorRefExposer } from "./layer-editor-ref-exposer/layer-editor-ref-exposer";
import { Model as RenderedModel } from './model/model';
import { ViewportShading, ViewportShadingSelect } from "./viewport-shading-select/viewport-shading-select";
import { Model } from "../../types/api/model/get-models-success-response";
import { ModelInModelTreeInLayerEditor } from "../../utils/create-model-tree-for-layer-editor-and-set-uuid/create-model-tree-for-layer-editor-and-set-uuid";

export const DEFAULT_CAMERA_POSITION = new THREE.Vector3(2, 2, 10);

export type LayerEditorRef = LayerEditorExposerRef & LayerEditorOrbitControlsRef;

export type LayerEditorActionStatus =
'nameTheLayerItemThenClickOnSelectModel' |
'selectingModel' |
'settingModel' |
'selectingSubmodel' |
'settingSubmodel' |
'clickOnSubmodelName' |
'settingSubmodelName'
;

type LayerEditorProps = {
    models: Model[];
    selectedModelUUID: string | null;
    workInstructions: WorkInstructionWithUUIDs[];
    isPOIActive: boolean;
    onSelectModel: (uuid: string | null) => void;
    onModelChange?: (uuid: string, data: {
        xPosition: number;
        yPosition: number;
        zPosition: number;
        xRotation: number;
        yRotation: number;
        zRotation: number;
    }) => void;
    instruction?: string;
    onModelLoaded?: (successfullyLoaded: boolean, model: ModelInModelTreeInLayerEditor) => void;
    viewportShading?: ViewportShading;
    onViewportShadingChange?: (viewportShading: ViewportShading) => void;
};

export const LayerEditor = forwardRef<LayerEditorRef, LayerEditorProps>((
    {
        models,
        isPOIActive,
        selectedModelUUID,
        workInstructions,
        onSelectModel,
        instruction,
        onModelChange,
        onModelLoaded,
        viewportShading,
        onViewportShadingChange
    },
    layerEditorRef
) => {
    const LayerEditorExposerRef = useRef<LayerEditorExposerRef>();
    const orbitControlsRef = useRef<LayerEditorOrbitControlsRef>();
    useImperativeHandle(layerEditorRef, () => ({
        getModelData: (uuid: string) => {
            if (LayerEditorExposerRef.current)
                LayerEditorExposerRef.current.getModelData(uuid);

            return undefined;
        },
        setModelPositionAndRotation: (uuid: string, position: THREE.Vector3Tuple, rotation: THREE.EulerTuple) => {
            if (LayerEditorExposerRef.current)
                LayerEditorExposerRef.current.setModelPositionAndRotation(uuid, position, rotation);
        },
        setModelMaterial: (uuid: string, material: THREE.Material) => {
            if (LayerEditorExposerRef.current) LayerEditorExposerRef.current.setModelMaterial(uuid, material);
        },
        setModelCenterToOrbitControlsTarget: (uuid: string) => {
            if (orbitControlsRef.current)
                orbitControlsRef.current.setModelCenterToOrbitControlsTarget(uuid);
        },
        zoomToModel: (uuid: string) => {
            if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(uuid);
        },
        setCameraPosition: (position: THREE.Vector3) => {
            if (orbitControlsRef.current) orbitControlsRef.current.setCameraPosition(position);
        }
    }), []);

    const createPOI = (workInstruction: WorkInstructionWithUUIDs) => {
        return (
            <POI
                key={workInstruction.id}
                POIUUID={workInstruction.POIUUID}
                POILineEndpointUUID={workInstruction.POILineEndpointUUID}
                workInstruction={workInstruction}
            />
        );
    };

    const createModel = (model: Model) => {
        if (!model.modifiedModel.url) return null;

        const handleClick = (e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();

            if (e.object) {
                setTimeout(() => {
                    onSelectModel(e.object.uuid);
                }, 300);
            }
        };

        const handleDoubleClick = (e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();

            if (e.object) {
                setTimeout(() => {
                    onSelectModel(e.object.uuid);
                    if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(e.object.uuid);
                }, 300);
            }
        };

        const handlePointerMissed = () => {
            setTimeout(() => {
                onSelectModel(null);
            }, 150);
        };

        const handleModelLoaded = (successfullyLoaded: boolean, model: ModelInModelTreeInLayerEditor) => {
            if (onModelLoaded) onModelLoaded(successfullyLoaded, model);
        };

        return (
            <RenderedModel
                key={model.uuid}
                uuid={model.uuid}
                visible={model.visible}
                name={model.name}
                position={[model.xPosition, model.yPosition, model.zPosition]}
                rotation={[model.xRotation, model.yRotation, model.zRotation]}
                scale={[model.xScale, model.yScale, model.zScale]}
                model={model.modifiedModel.url}
                onClick={handleClick}
                onPointerMissed={handlePointerMissed}
                onModelLoaded={handleModelLoaded}
                onDoubleClick={handleDoubleClick}
            />
        );
    };

    const handleModelChange = (
        uuid: string,
        xPosition: number,
        yPosition: number,
        zPosition: number,
        xRotation: number,
        yRotation: number,
        zRotation: number
    ) => {
        if (onModelChange) onModelChange(uuid, { xPosition, yPosition, zPosition, xRotation, yRotation, zRotation });
    };

    return (
        <div className="layer-editor-container">

            <Menu />

            {
                instruction && (
                    <div className="instruction">
                        { instruction }
                    </div>
                )
            }

            <div className="position-relative">
            
                {
                    viewportShading && (
                        <div className="viewport-shading-select-container">
                            <ViewportShadingSelect
                                viewportShading={viewportShading}
                                onViewportShadingChange={
                                    (viewportShading) => {
                                        if (onViewportShadingChange) onViewportShadingChange(viewportShading);
                                    }
                                }
                            />
                        </div>
                    )
                }

                <Canvas
                    className="canvas"
                    camera={
                        {
                            position: DEFAULT_CAMERA_POSITION,
                            near: 0.01,
                            far: 1000,
                            fov: 50
                        }
                    }
                >

                    {
                        isPOIActive && workInstructions && workInstructions.map(workInstruction => createPOI(workInstruction))
                    }

                    { models.map(model => createModel(model)) }

                    <BoxHelper selectedModelUUID={selectedModelUUID} />
                    <OrbitControls selectedModelUUID={selectedModelUUID} ref={orbitControlsRef as React.LegacyRef<LayerEditorOrbitControlsRef>} />
                    <TransformControls
                        selectedModelUUID={selectedModelUUID}
                        skipSettingModelData={false}
                        onModelChange={handleModelChange}
                    />

                    <ambientLight />
                    <directionalLight position={new THREE.Vector3(0, 1000, 0)} />
                    <directionalLight position={new THREE.Vector3(0, -1000, 0)} />
                    <directionalLight position={new THREE.Vector3(1000, 0, 0)} />
                    <directionalLight position={new THREE.Vector3(-1000, 0, 0)} />
                    <directionalLight position={new THREE.Vector3(0, 0, 1000)} />
                    <directionalLight position={new THREE.Vector3(0, 0, -1000)} />

                    <gridHelper />
                    <color attach='background' args={['#ffffffff']} />
                    <LayerEditorRefExposer ref={LayerEditorExposerRef} />

                </Canvas>

            </div>

        </div>
    );
});